/**
 * syncTranslatedCaptionText.ts
 *
 * Re-text an existing translated karaoke track from the current translation
 * phrases, WITHOUT re-segmenting it.
 *
 * Why this exists: the captions render path bakes each clip's text from
 * `TranslationPhrase.translatedValue` at produce time, so a translation edit
 * ("Fix Translation", the phrase popover, a revert) has to be pushed into the
 * baked track. Re-running `ProduceKaraokeCommand` did that, but it rebuilds the
 * track from the producer's segmentation — which discards every user edit to the
 * caption STRUCTURE: synthetic line breaks (`CaptionLineBreak`) and merged rows
 * (`MergeAdjacentCaptionClipsCommand`). Those are exactly the edits a re-produce
 * cannot recover, since a merge leaves no record at all.
 *
 * So this module rewrites text in place instead: clip count, clip boundaries,
 * token arrays, offsets and sibling groups are all left untouched, and only
 * `translatedText` / `translatedPhrases[].text` (plus the derived intra-phrase
 * split times) change — and the clip's soft line breaks, which index into
 * `translatedPhrases` and so follow it (PLAT-11914).
 *
 * Matching is by COVERING active phrase, not by exact `originalIndices`
 * equality, which makes all three shapes fall out of one rule:
 *
 *   - 1 entry  → 1 phrase: plain re-text.
 *   - N entries → 1 phrase: the phrase is spread across N caption rows (a
 *     synthetic break, or a "Fix Translation" override segment that subsumed
 *     several producer fragments). The new text is redistributed across those N
 *     rows in proportion to their current caption-unit counts, so the user's line
 *     structure survives.
 *   - 1 entry per phrase on a merged clip: each entry re-texts independently and
 *     the merged row is rejoined.
 *
 * When an edit leaves FEWER words than the slots a phrase occupies, what happens
 * depends on whether those slots are real rows:
 *
 *   - Slots inside ONE clip are phrase units within a single row, not rows. The
 *     row can always show the new text, so they FUSE into one unit in place —
 *     nothing structural is lost. This is the merged-row-edited-as-one-span case.
 *   - Slots across SEPARATE clips are rows, and a one-word phrase cannot occupy
 *     two of them. The user's line break has stopped being representable, so the
 *     surplus rows are reported in `collapseClipIndices` for the caller to merge
 *     (which also retires the `CaptionLineBreak` record) before re-syncing. Only
 *     the break that no longer fits is dropped, and it is undoable.
 *
 * Pure — no editor, no MobX, no IO. `SyncTranslatedCaptionTextCommand` applies
 * the result.
 */

import { joinCaptionRowTexts } from './karaokeSegmentation';
import {
  computeCaptionUnitSpans,
  computeProportionAtTextPosition,
} from './karaokeWordTiming';

import type { PhraseSoftBreak } from './captionSoftBreaks';
import type { TranslationPhrase } from '@src/types.ts/translation';

/** Structural shape of one `KaraokeLine.translatedPhrases` entry. */
export interface CaptionPhraseEntry {
  text: string;
  ts: number;
  endTs: number;
  originalIndices?: number[];
}

/** The fields a text sync rewrites on a clip. */
export interface CaptionTextPatch {
  translatedText: string;
  translatedPhrases: CaptionPhraseEntry[];
  /**
   * PLAT-11914 — the clip's soft breaks against the new `translatedPhrases`:
   * an entry whose text is unchanged keeps its breaks at its new index, and a
   * re-texted or retired entry loses them (an offset into the old wording
   * means nothing in the new one). `undefined` when none survive.
   */
  softBreakPhrasePositions: PhraseSoftBreak[] | undefined;
}

export interface SyncTranslatedCaptionTextResult {
  /** Per-clip patch, index-aligned with the input clips. `null` ⇒ unchanged. */
  patches: (CaptionTextPatch | null)[];
  /**
   * Clip indices whose caption row must be merged into its LEFT neighbour
   * before the edited text can fit, ascending. Emitted when a phrase's new text
   * has fewer words than the rows it is split across: the user's line break is
   * no longer representable (a one-word phrase cannot occupy two rows), so the
   * surplus rows collapse rather than the text going stale. The caller merges
   * these — `MergeAdjacentCaptionClipsCommand` also drops the matching
   * `CaptionLineBreak` record — and then re-runs the sync, at which point the
   * text fits. Phrases in this list are NOT re-texted in the same pass.
   */
  collapseClipIndices: number[];
  /**
   * Count of phrases left stale because their text did not fit AND their rows
   * were not in the safely-collapsible shape. Callers log this; not an error.
   */
  staleTooShort: number;
}

/** Minimal clip shape this service reads. */
interface CaptionClipLike {
  translatedPhrases?: readonly CaptionPhraseEntry[];
  softBreakPhrasePositions?: readonly PhraseSoftBreak[];
}

export interface CaptionTextSource {
  /**
   * Producer-authored caption text per phrase, keyed by the phrase's LEAD source
   * index — build it with `resolvePhraseCaptionTexts` and pass it in.
   *
   * REQUIRED for correctness whenever the caller can supply it. A phrase's
   * caption text is NOT `phrase.translatedValue`: punctuation that gets no row
   * of its own is folded in by the producer, so a row reads `'… Dingo.'` while
   * the phrase's value is still `'… Dingo'`. Comparing against the raw value
   * reported every folded row permanently stale and, on sync, wrote the
   * unfolded value back — deleting that punctuation again.
   *
   * Omitted only by callers with no transcript tokens to resolve from (and by
   * older tests); the fallback to `translatedValue` then applies.
   */
  captionTextByLeadIndex?: ReadonlyMap<number, string>;
}

/**
 * Spread `target` across `entries` rows, allocating caption units in proportion
 * to each row's CURRENT unit count so a two-line caption stays two lines of
 * roughly the same balance. Units are what a row may break between
 * (`computeCaptionUnitSpans`, PLAT-11836): words, or characters in Japanese, so
 * a break the user placed inside a dictionary word stays where it is. Rows are literal substrings of `target` (PLAT-11480), and the
 * intra-phrase boundary times are derived from `target` itself with
 * `computeProportionAtTextPosition` — the same helper
 * `AddSyntheticCaptionBreakCommand` uses — so the canvas highlight still crosses
 * a row boundary exactly where the text does.
 *
 * Returns `null` when `target` has fewer units than there are rows: every row
 * must keep at least one unit, and leaving one phrase's text briefly stale is
 * better than rendering a blank caption row. The caller reports the count.
 */
function redistribute(
  entries: readonly CaptionPhraseEntry[],
  target: string,
): CaptionPhraseEntry[] | null {
  const rows = entries.length;
  const units = computeCaptionUnitSpans(target);
  if (units.length < rows) return null;

  // `Math.max(1, …)` guards a row that is currently empty from skewing the
  // proportion to zero.
  const currentCounts = entries.map(e =>
    Math.max(1, computeCaptionUnitSpans(e.text).length),
  );
  const currentTotal = currentCounts.reduce((sum, c) => sum + c, 0);

  // Proportional split with a one-unit floor per row; the last row takes the
  // remainder so the allocation always sums to `units.length` exactly.
  const allocation: number[] = [];
  let assigned = 0;
  for (let i = 0; i < rows - 1; i++) {
    const wanted = Math.round((units.length * currentCounts[i]) / currentTotal);
    // Never consume the units the remaining rows still need.
    const available = units.length - assigned - (rows - 1 - i);
    allocation.push(Math.min(Math.max(1, wanted), available));
    assigned += allocation[i];
  }
  allocation.push(units.length - assigned);

  // Rows are LITERAL SUBSTRINGS of the target (PLAT-11480): each row spans
  // from its first unit's offset to its last unit's end. No joiner is ever
  // re-invented — Japanese rows stay spaceless, Korean and French spacing
  // survives verbatim, and the proportion lookups run against the target
  // itself, so offsets always align with its unit boundaries.
  const texts: string[] = [];
  const rowStart: number[] = [];
  const rowEnd: number[] = [];
  let unit = 0;
  for (const count of allocation) {
    const start = units[unit].start;
    const end = units[unit + count - 1].end;
    rowStart.push(start);
    rowEnd.push(end);
    texts.push(target.slice(start, end));
    unit += count;
  }

  const windowStart = entries[0].ts;
  const windowSpan = entries[rows - 1].endTs - windowStart;

  const out: CaptionPhraseEntry[] = [];
  for (let i = 0; i < rows; i++) {
    const startProportion =
      i === 0 ? 0 : computeProportionAtTextPosition(target, rowStart[i]);
    const endProportion =
      i === rows - 1 ? 1 : computeProportionAtTextPosition(target, rowEnd[i]);
    out.push({
      ...entries[i],
      text: texts[i],
      ts: windowStart + startProportion * windowSpan,
      endTs: windowStart + endProportion * windowSpan,
    });
  }
  return out;
}

/**
 * True when every slot sits in the SAME clip on contiguous entry indexes.
 *
 * Entries inside one clip are phrase units within a single caption ROW, not
 * separate rows — the row renders them joined by spaces. So the "one row per
 * word" limit does not apply to them: however short the edit gets, the row can
 * always show it, by fusing the units into one. Contiguity is required because
 * fusing non-adjacent entries would reorder the row's text around whatever sits
 * between them.
 */
function isFusableRunInOneClip(
  slots: readonly { clipIndex: number; entryIndex: number }[],
): boolean {
  for (let i = 1; i < slots.length; i++) {
    if (slots[i].clipIndex !== slots[0].clipIndex) return false;
    if (slots[i].entryIndex !== slots[i - 1].entryIndex + 1) return false;
  }
  return true;
}

/**
 * Which of a phrase's surplus ROWS can be collapsed away, as clip indices to
 * merge into their left neighbour.
 *
 * Only the synthetic-break shape is collapsible this way: exactly one row per
 * clip, in CONSECUTIVE clips. Slots sharing a clip are not rows at all and are
 * handled by {@link isFusableRunInOneClip} before this is reached; anything else
 * returns `null` and the caller leaves the phrase stale.
 *
 * `keep` is one row per unit (at least one row), so after the merges the row
 * count is exactly what `redistribute` can fill.
 */
function planCollapse(
  slots: readonly { clipIndex: number }[],
  target: string,
): number[] | null {
  const keep = Math.max(1, computeCaptionUnitSpans(target).length);
  if (slots.length <= keep) return null;
  for (let i = 1; i < slots.length; i++) {
    if (slots[i].clipIndex !== slots[i - 1].clipIndex + 1) return null;
  }
  return slots.slice(keep).map(slot => slot.clipIndex);
}

/**
 * PLAT-11914 — a clip's soft breaks against its patched entries. `entries` is
 * still index-aligned with the clip's own `translatedPhrases` (retired entries
 * are only filtered out of the patch), so each break's entry can be checked:
 * retired or re-texted drops it, unchanged moves it to the entry's index among
 * the survivors. A break that would then open the caption is dropped too.
 */
function rebaseSoftBreaks(
  clip: CaptionClipLike,
  entries: readonly CaptionPhraseEntry[],
  isDropped: (entryIndex: number) => boolean,
): PhraseSoftBreak[] | undefined {
  const newIndex = new Map<number, number>();
  let survivors = 0;
  entries.forEach((entry, entryIndex) => {
    if (isDropped(entryIndex)) return;
    if (entry.text === clip.translatedPhrases?.[entryIndex]?.text) {
      newIndex.set(entryIndex, survivors);
    }
    survivors += 1;
  });
  const rebased = (clip.softBreakPhrasePositions ?? []).flatMap(b => {
    const phraseIndex = newIndex.get(b.phraseIndex);
    if (phraseIndex == null || (phraseIndex === 0 && b.offset <= 0)) return [];
    return [{ phraseIndex, offset: b.offset }];
  });
  return rebased.length > 0 ? rebased : undefined;
}

/**
 * Compute the per-clip text patches that bring `clips` in line with `phrases`.
 *
 * Only ACTIVE phrases (`active !== false`) participate — a retired fragment
 * superseded by an override segment must not win over the override that
 * replaced it. Caption entries whose source indices no phrase covers are left
 * untouched (nothing authoritative to re-text them from).
 */
export function computeTranslatedCaptionTextPatches(
  clips: readonly CaptionClipLike[],
  phrases: readonly TranslationPhrase[],
  source: CaptionTextSource = {},
): SyncTranslatedCaptionTextResult {
  const phraseByIndex = new Map<number, TranslationPhrase>();
  for (const phrase of phrases) {
    if (phrase.active === false) continue;
    for (const index of phrase.originalIndices) {
      phraseByIndex.set(index, phrase);
    }
  }

  // Group the caption entries under their covering phrase, in track order, so a
  // phrase spread across several rows is redistributed as one unit.
  interface Slot {
    clipIndex: number;
    entryIndex: number;
    entry: CaptionPhraseEntry;
  }
  const groups = new Map<TranslationPhrase, Slot[]>();
  clips.forEach((clip, clipIndex) => {
    clip.translatedPhrases?.forEach((entry, entryIndex) => {
      const lead = entry.originalIndices?.[0];
      if (lead === undefined) return;
      const phrase = phraseByIndex.get(lead);
      if (!phrase) return;
      const slot = { clipIndex, entryIndex, entry };
      const existing = groups.get(phrase);
      if (existing) existing.push(slot);
      else groups.set(phrase, [slot]);
    });
  });

  // Copy-on-write: a clip only materialises a new entry array once something in
  // it actually changes, so untouched clips return a `null` patch.
  const rewritten: (CaptionPhraseEntry[] | null)[] = clips.map(() => null);
  const entriesFor = (clipIndex: number): CaptionPhraseEntry[] =>
    (rewritten[clipIndex] ??= (clips[clipIndex].translatedPhrases ?? []).map(
      entry => ({ ...entry }),
    ));

  // Entries retired by an in-row fusion, keyed `clipIndex:entryIndex`. Recorded
  // rather than spliced out immediately: other groups hold entry indexes for the
  // same clip that were captured above, and splicing mid-loop would shift them.
  // Filtered once, when the patches are built.
  const dropped = new Set<string>();

  let staleTooShort = 0;
  const collapseClipIndices: number[] = [];
  for (const [phrase, slots] of groups) {
    // PLAT-11340 follow-up — a phrase with NO translation left (the reviewer
    // used "Remove Translation", which stores the correction as an empty
    // value). Re-texting its rows in place would leave a blank caption still
    // holding its time window, and — once every row in a clip empties — an
    // empty row that still karaoke-highlights. Retire the entries instead: the
    // `dropped` filter below rebuilds `translatedText` without them, and a clip
    // left with none renders nothing rather than an empty row.
    //
    // Keyed on the PHRASE's value, not on the resolved `target`: the producer
    // folds row-less punctuation into a row's text, so a removed phrase that
    // happens to carry a folded comma resolves to "," and would slip past an
    // emptiness test on `target`. `entriesFor` is called for its
    // copy-on-write side effect — without it the clip materialises no patch
    // and the entries are never filtered.
    //
    // Deliberately NOT routed through `planCollapse`, which floors at one kept
    // row (`Math.max(1, …)`) because non-empty text must always land
    // somewhere. A removal has nowhere to land — that is the point of it.
    if ((phrase.translatedValue ?? '').trim() === '') {
      for (const slot of slots) {
        entriesFor(slot.clipIndex);
        dropped.add(`${slot.clipIndex}:${slot.entryIndex}`);
      }
      continue;
    }

    // The producer is the authority on a row's text — it folds in punctuation
    // that has no row of its own. Never re-derive that here.
    const target =
      source.captionTextByLeadIndex?.get(phrase.originalIndices[0]) ??
      phrase.translatedValue;

    if (slots.length === 1) {
      const [slot] = slots;
      if (slot.entry.text === target) continue;
      entriesFor(slot.clipIndex)[slot.entryIndex].text = target;
      continue;
    }

    // Unchanged text keeps its rows exactly — re-spreading it would move the
    // user's breaks for an edit to some other phrase (PLAT-11836).
    if (joinCaptionRowTexts(slots.map(slot => slot.entry.text)) === target) {
      continue;
    }

    const spread = redistribute(
      slots.map(slot => slot.entry),
      target,
    );
    if (!spread) {
      // The text no longer fills the slots. If they are phrase units inside ONE
      // row (a merged row edited as a single span), fuse them: the first unit
      // takes the whole text over the combined time window and the rest retire.
      // No line structure is lost — the row already rendered them as one line.
      if (isFusableRunInOneClip(slots)) {
        const first = slots[0];
        const last = slots[slots.length - 1];
        const entries = entriesFor(first.clipIndex);
        entries[first.entryIndex] = {
          text: target,
          ts: first.entry.ts,
          endTs: last.entry.endTs,
          originalIndices: [...phrase.originalIndices],
        };
        for (let i = 1; i < slots.length; i++) {
          dropped.add(`${slots[i].clipIndex}:${slots[i].entryIndex}`);
        }
        continue;
      }
      // Separate rows. Collapse the surplus ones if their shape allows it; the
      // caller merges and re-syncs, and the text then fits.
      const plan = planCollapse(slots, target);
      if (plan) collapseClipIndices.push(...plan);
      else staleTooShort += 1;
      continue;
    }
    // Skip a no-op spread so an unrelated edit doesn't dirty every split row.
    if (spread.every((piece, i) => piece.text === slots[i].entry.text))
      continue;
    spread.forEach((piece, i) => {
      const slot = slots[i];
      const entry = entriesFor(slot.clipIndex)[slot.entryIndex];
      entry.text = piece.text;
      entry.ts = piece.ts;
      entry.endTs = piece.endTs;
    });
  }

  return {
    patches: rewritten.map((entries, clipIndex) => {
      if (!entries) return null;
      const isDropped = (entryIndex: number) =>
        dropped.has(`${clipIndex}:${entryIndex}`);
      const kept = dropped.size
        ? entries.filter((_, entryIndex) => !isDropped(entryIndex))
        : entries;
      return {
        translatedPhrases: kept,
        // Same CJK-aware join every caption-row composer uses (PLAT-11468).
        translatedText: joinCaptionRowTexts(kept.map(entry => entry.text)),
        softBreakPhrasePositions: rebaseSoftBreaks(
          clips[clipIndex],
          entries,
          isDropped,
        ),
      };
    }),
    // Ascending so the caller can resolve every clip id before the first merge
    // shifts the array.
    collapseClipIndices: [...collapseClipIndices].sort((a, b) => a - b),
    staleTooShort,
  };
}

/** Outcome of a read-only staleness check. */
export interface TranslatedCaptionStaleness {
  /** True when a sync would change anything at all. */
  isStale: boolean;
  /**
   * How many caption ROWS a sync would rewrite — NOT how many edits are
   * outstanding. `patches` is per-clip, and one edit routinely spans several
   * rows (a span edit across three rows rewrites three clips) while editing the
   * same phrase twice shows up as one. Use it for logging; never present it to
   * the user as a count of edits.
   */
  staleRowCount: number;
}

/**
 * Read-only check for whether the baked captions have drifted from the current
 * translation.
 *
 * A dry run of {@link computeTranslatedCaptionTextPatches}, which already
 * computes exactly what a sync would change — so there is no second comparison
 * implementation to keep in step. Nothing is mutated: that function is
 * copy-on-write, and the rewrites live only on the patches, which are discarded
 * here.
 *
 * Drives the "Refresh Captions" notice rather than an automatic sync, because a
 * whole-track sync also discards caption structure edits (line breaks, merged
 * rows) and so is the user's call.
 */
export function detectStaleTranslatedCaptions(
  clips: readonly CaptionClipLike[],
  phrases: readonly TranslationPhrase[],
  source: CaptionTextSource = {},
): TranslatedCaptionStaleness {
  const { patches } = computeTranslatedCaptionTextPatches(
    clips,
    phrases,
    source,
  );
  const staleRowCount = patches.reduce(
    (count, patch) => (patch ? count + 1 : count),
    0,
  );
  return { isStale: staleRowCount > 0, staleRowCount };
}
